import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ArrowLeft } from "lucide-react";
import api, { errMsg } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { MASTER_DEFS } from "../config/masterDefs";

export default function Masters() {
  const { mtype } = useParams();
  const navigate = useNavigate();
  const def = MASTER_DEFS[mtype];
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const can = (p) => isAdmin || (user.permissions || []).includes(p);
  const cols = def ? [["name", `${def.label} Name`], ...def.fields] : [];
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    if (!def) return;
    try { const { data } = await api.get(`/masters/${mtype}`, { params: { search } }); setRows(data); } catch { setRows([]); }
  }, [mtype, search, def]);
  useEffect(() => { load(); }, [load]);

  if (!def) return <div className="p-8 text-slate-500">Unknown master type.</div>;

  const save = async () => {
    if (!(form.name || "").trim()) return toast.error("Name is required.");
    try {
      if (editing) await api.put(`/masters/${mtype}/${editing.id}`, form);
      else {
        const { data } = await api.post(`/masters/${mtype}`, form);
        if (data._duplicate) toast.warning("This record already exists.");
      }
      toast.success("Saved"); setModal(false); setForm({}); setEditing(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (r) => { if (!window.confirm("Delete this master record?")) return; try { await api.delete(`/masters/${mtype}/${r.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHeader title={`${def.label} Master`} subtitle={`${rows.length} entries`} actions={
        <div className="flex gap-2">
          <button className="btn-outline btn-sm" onClick={() => navigate("/masters")}><ArrowLeft className="h-4 w-4" /> All Masters</button>
          {can("add_master") && <button className="btn-accent" onClick={() => { setEditing(null); setForm({}); setModal(true); }} data-testid={`${mtype}-master-add`}><Plus className="h-4 w-4" /> Add</button>}
        </div>} />
      <div className="p-6 lg:p-8 space-y-4">
        <input className="input max-w-sm" placeholder={`Search ${def.label}...`} value={search} onChange={(e) => setSearch(e.target.value)} data-testid={`${mtype}-master-search`} />
        <div className="card overflow-hidden"><div className="overflow-x-auto"><table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>{cols.map(([, l]) => <th key={l} className="th">{l}</th>)}<th className="th">Actions</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={cols.length + 1}>No entries yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`${mtype}-master-row-${r.id}`}>
                {cols.map(([k]) => <td key={k} className="td">{r[k] || "—"}</td>)}
                <td className="td"><div className="flex gap-1">
                  {can("edit_master") && <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => { setEditing(r); setForm(r); setModal(true); }} data-testid={`${mtype}-master-edit-${r.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>}
                  {can("delete_master") && <button className="p-1.5 rounded hover:bg-red-50" onClick={() => del(r)} data-testid={`${mtype}-master-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table></div></div>
      </div>
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(false)}>
          <div className="card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid={`${mtype}-master-modal`}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-head text-xl font-bold">{editing ? "Edit" : "Add"} {def.label}</h3><button onClick={() => setModal(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="grid md:grid-cols-2 gap-4">
              {cols.map(([k, l]) => <div key={k} className={k === "address" ? "md:col-span-2" : ""}><label className="label">{l}{k === "name" ? " *" : ""}</label><input className="input" value={form[k] || ""} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} data-testid={`${mtype}-master-${k}`} /></div>)}
            </div>
            <div className="flex justify-end gap-2 mt-6"><button className="btn-outline" onClick={() => setModal(false)}>Cancel</button><button className="btn-primary" onClick={save} data-testid={`${mtype}-master-save`}>Save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
